/**
 * What a one-time sale actually costs (spec §10 capital gains).
 *
 * A big long-term gain is never just "15% of the gain": it stacks on top of
 * ordinary income, can push part of itself from 15% into 20%, can drag the
 * 3.8% net investment income surtax along, wipes out an ACA subsidy in the
 * year it lands, and raises Medicare premiums two years later. The only honest
 * way to price it is to run the whole plan with and without the event and
 * report the difference — which is exactly what this does.
 */
import type { Plan } from '../model/types';
import { runProjection } from './projection';

export interface SaleImpact {
  year: number;
  proceeds: number;
  costBasis: number;
  gain: number;

  /** Federal capital-gains tax attributable to the sale. */
  capitalGainsTax: number;
  /** Net investment income surtax attributable to the sale. */
  niit: number;
  /** Extra ordinary-rate tax caused by the sale (SS becoming taxable, etc.). */
  otherFederalTax: number;
  /** State tax attributable to the sale. */
  stateTax: number;
  /** ACA premium credit lost in the sale year (0 if not on ACA). */
  acaSubsidyLost: number;
  /** Extra Medicare premiums in the two years after, via IRMAA's lookback. */
  irmaaSurcharge: number;
  /** Everything above: the true all-in cost of the sale year. */
  totalCost: number;

  /** Share of the proceeds consumed by that cost. */
  effectiveRateOnProceeds: number;
  /** Top capital-gains rate the gain reached (0, 0.15 or 0.20). */
  topCapitalGainsRate: number;
  /** True when part of the gain landed in the 20% bracket. */
  crossedIntoTopRate: boolean;
  /** Net worth at the end of the plan, with the sale minus without it. */
  endingNetWorthDelta: number;
}

/** Sum a field across the sale year and the two IRMAA-lookback years after. */
function windowSum(
  values: { year: number; v: number }[],
  from: number,
  to: number,
): number {
  return values.filter((x) => x.year >= from && x.year <= to).reduce((t, x) => t + x.v, 0);
}

/**
 * Price a single 'ltcg' event. Returns null when the event is missing, is not
 * a capital gain, or is an outflow — nothing to analyze in those cases.
 */
export function analyzeSale(plan: Plan, eventId: string, startYear = 2026): SaleImpact | null {
  const event = plan.oneTimeEvents.find((e) => e.id === eventId);
  if (!event || event.taxTreatment !== 'ltcg' || event.amount <= 0) return null;

  const withSale = runProjection(plan, startYear);
  const without = runProjection(
    { ...plan, oneTimeEvents: plan.oneTimeEvents.filter((e) => e.id !== eventId) },
    startYear,
  );

  const year = event.year;
  const basis = event.costBasis ?? 0;
  const gain = Math.max(0, event.amount - basis);

  const a = withSale.snapshots.find((s) => s.year === year);
  const b = without.snapshots.find((s) => s.year === year);
  if (!a || !b) return null;

  const capitalGainsTax = Math.max(0, a.federal.capitalGainsTax - b.federal.capitalGainsTax);
  const niit = Math.max(0, a.federal.niit - b.federal.niit);
  const otherFederalTax = Math.max(0, a.federal.ordinaryTax - b.federal.ordinaryTax);
  const stateTax = Math.max(0, a.stateTax - b.stateTax);
  const acaSubsidyLost = Math.max(0, b.acaSubsidy - a.acaSubsidy);

  // IRMAA looks back two years, so a sale shows up in Medicare premiums in the
  // two years that follow it.
  const irmaaSurcharge = Math.max(
    0,
    windowSum(withSale.snapshots.map((s) => ({ year: s.year, v: s.healthcarePremiums })), year + 1, year + 2)
      - windowSum(without.snapshots.map((s) => ({ year: s.year, v: s.healthcarePremiums })), year + 1, year + 2),
  );

  const totalCost =
    capitalGainsTax + niit + otherFederalTax + stateTax + acaSubsidyLost + irmaaSurcharge;

  // The top rate the gain itself reached: compare the effective rate on the
  // preferential slice against the statutory steps.
  const preferential = a.federal.preferentialIncome;
  const cgRate = preferential > 0 ? a.federal.capitalGainsTax / preferential : 0;
  const topCapitalGainsRate = cgRate > 0.155 ? 0.2 : cgRate > 0.005 ? 0.15 : 0;

  const lastWith = withSale.snapshots[withSale.snapshots.length - 1];
  const lastWithout = without.snapshots[without.snapshots.length - 1];

  return {
    year,
    proceeds: event.amount,
    costBasis: basis,
    gain,
    capitalGainsTax,
    niit,
    otherFederalTax,
    stateTax,
    acaSubsidyLost,
    irmaaSurcharge,
    totalCost,
    effectiveRateOnProceeds: event.amount > 0 ? totalCost / event.amount : 0,
    topCapitalGainsRate,
    crossedIntoTopRate: topCapitalGainsRate === 0.2,
    endingNetWorthDelta: (lastWith?.netWorth ?? 0) - (lastWithout?.netWorth ?? 0),
  };
}
